/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import { AnyAction } from 'redux';
import {
    SET_GRID_GUIDE_START_TIME,
    SET_GRID_GUIDE_OFFSET,
    SET_GRID_GUIDE_STATION_IDS,
    LOAD_GRID_GUIDE_CHANNELS,
    LOAD_GRID_GUIDE_ROWS,
    SET_GRID_GUIDE_TIMESTAMP_WINDOW,
    LOAD_GRID_GUIDE_PAST_ROWS,
    LOAD_GRID_GUIDE_FUTURE_ROWS,
    RESET_GRID_GUIDE_ROWS,
    RESET_GRID_GUIDE_CHANNELS,
    SET_IS_PAST_GUIDE_MODE,
    ON_LEFT_SET_PREVIOUS_PAGE,
    ON_RIGHT_SET_NEXT_PAGE,
    SET_CHANNELS_COUNT,
    SET_IS_CHANNELS_SERVICE_FAIL,
    SET_GUIDE_ROWS_SERVICE_FAIL,
    REFRESH_GRID_GUIDE,
    SET_GRID_GUIDE_ROWS,
    SET_GRID_GUIDE_ROWS_CACHE,
    SET_GRID_GUIDE_CHANNELS_CACHE,
    SET_GRID_GUIDE_CACHE,
    SET_GRID_GUIDE_OFFER_PREVIEW_DATA,
    GUIDE_SET_IS_CARET_VISIBLE,
    GUIDE_SET_ERROR,
    GUIDE_SET_IS_SPINNER_ACTIVE,
} from './guide.types';
import { Channel } from '../../core/entities/Channel';
import { GridGuideRow } from '../../core/entities/GridGuideRow';
import { GuideError } from '../../core/error';

export const setGuideStartTimeAction = (
    startTime: Date,
    previousTimestamp: Date,
    currentTimestamp: Date,
    nextTimestamp: Date,
): AnyAction => ({
    type: SET_GRID_GUIDE_START_TIME,
    payload: {
        startTime,
        previousTimestamp,
        currentTimestamp,
        nextTimestamp,
    },
});

export const setGridGuideOffsetAction = (offset: number): AnyAction => ({
    type: SET_GRID_GUIDE_OFFSET,
    payload: {
        offset,
    },
});

export const setGridGuideStationIdsAction = (stationIds: string[]): AnyAction => ({
    type: SET_GRID_GUIDE_STATION_IDS,
    payload: {
        stationIds,
    },
});

export const guideSetErrorAction = (error: GuideError | null): AnyAction => ({
    type: GUIDE_SET_ERROR,
    payload: {
        error,
    },
});

export const setGridGuideTimestampWindowAction = (
    previousTimestamp: Date,
    currentTimestamp: Date,
    nextTimestamp: Date,
): AnyAction => ({
    type: SET_GRID_GUIDE_TIMESTAMP_WINDOW,
    payload: {
        previousTimestamp,
        currentTimestamp,
        nextTimestamp,
    },
});

export const resetGridGuideRowsAction = (): AnyAction => ({
    type: RESET_GRID_GUIDE_ROWS,
    payload: {
        pastGuideRows: [],
        guideRows: [],
        futureGuideRows: [],
    },
});

export const setGridGuideRowsAction = (
    pastGuideRows: GridGuideRow[],
    guideRows: GridGuideRow[],
    futureGuideRows: GridGuideRow[],
): AnyAction => ({
    type: SET_GRID_GUIDE_ROWS,
    payload: {
        pastGuideRows,
        guideRows,
        futureGuideRows,
    },
});

export const loadGridGuideChannelsAction = (
    channels: Channel[],
    channelsCount: number,
    stationIds: string[],
    isChannelsServiceFail: boolean,
): AnyAction => ({
    type: LOAD_GRID_GUIDE_CHANNELS,
    payload: {
        channels,
        channelsCount,
        stationIds,
        isChannelsServiceFail,
    },
});

export const loadGridGuideRowsAction = (guideRows: GridGuideRow[]): AnyAction => ({
    type: LOAD_GRID_GUIDE_ROWS,
    payload: {
        guideRows,
        isGuideRowsServiceFail: false,
    },
});

export const loadGridGuidePastRowsAction = (pastGuideRows: GridGuideRow[]): AnyAction => ({
    type: LOAD_GRID_GUIDE_PAST_ROWS,
    payload: {
        pastGuideRows,
    },
});

export const loadGridGuideFutureRowsAction = (futureGuideRows: GridGuideRow[]): AnyAction => ({
    type: LOAD_GRID_GUIDE_FUTURE_ROWS,
    payload: {
        futureGuideRows,
    },
});

export const resetGridGuideChannelsAction = (): AnyAction => ({
    type: RESET_GRID_GUIDE_CHANNELS,
    payload: {
        channels: [],
    },
});

export const setIsPastGuideModeAction = (isPastGuideMode: boolean): AnyAction => ({
    type: SET_IS_PAST_GUIDE_MODE,
    payload: {
        isPastGuideMode,
    },
});

export const onLeftSetPreviousPageAction = (
    previousTimestamp: Date,
    currentTimestamp: Date,
    nextTimestamp: Date,
    guideRows: GridGuideRow[],
    pastGuideRows: GridGuideRow[],
): AnyAction => ({
    type: ON_LEFT_SET_PREVIOUS_PAGE,
    payload: {
        previousTimestamp,
        currentTimestamp,
        nextTimestamp,
        futureGuideRows: guideRows,
        guideRows: pastGuideRows,
    },
});

export const onRightSetNextPageAction = (
    previousTimestamp: Date,
    currentTimestamp: Date,
    nextTimestamp: Date,
    guideRows: GridGuideRow[],
    futureGuideRows: GridGuideRow[],
): AnyAction => ({
    type: ON_RIGHT_SET_NEXT_PAGE,
    payload: {
        previousTimestamp,
        currentTimestamp,
        nextTimestamp,
        pastGuideRows: guideRows,
        guideRows: futureGuideRows,
    },
});

export const setChannelsCountAction = (channelsCount: number): AnyAction => ({
    type: SET_CHANNELS_COUNT,
    payload: {
        channelsCount,
    },
});

export const setIsChannelsServiceFailAction = (isChannelsServiceFail: boolean): AnyAction => ({
    type: SET_IS_CHANNELS_SERVICE_FAIL,
    payload: {
        isChannelsServiceFail,
    },
});

export const setIsGuideRowsServiceFailAction = (
    isGuideRowsServiceFail: boolean,
    dummyGuideRows: GridGuideRow[],
    previousTimestamp: Date,
    currentTimestamp: Date,
    nextTimestamp: Date,
): AnyAction => ({
    type: SET_GUIDE_ROWS_SERVICE_FAIL,
    payload: {
        isGuideRowsServiceFail,
        previousTimestamp,
        currentTimestamp,
        nextTimestamp,
        guideRows: dummyGuideRows,
        pastGuideRows: dummyGuideRows,
        futureGuideRows: dummyGuideRows,
    },
});

export const refreshGridGuideAction = (
    startTime: Date,
    currentTimestamp: Date,
    nextTimestamp: Date,
    previousTimestamp: Date,
): AnyAction => ({
    type: REFRESH_GRID_GUIDE,
    payload: {
        startTime,
        currentTimestamp,
        nextTimestamp,
        previousTimestamp,
        isPastGuideMode: false,
        guideRows: [],
        pastGuideRows: [],
        futureGuideRows: [],
    },
});

export const setGridGuideRowsCacheAction = (
    guideRowsCache: Array<GridGuideRow[][]>,
): AnyAction => ({
    type: SET_GRID_GUIDE_ROWS_CACHE,
    payload: {
        guideRowsCache,
    },
});

export const setGridGuideChannelsCacheAction = (channelsCache: Array<Channel[]>): AnyAction => ({
    type: SET_GRID_GUIDE_CHANNELS_CACHE,
    payload: {
        channelsCache,
    },
});

export const setGridGuideCacheAction = (
    channelsCache: Array<Channel[]>,
    guideRowsCache: Array<GridGuideRow[][]>,
): AnyAction => ({
    type: SET_GRID_GUIDE_CACHE,
    payload: {
        channelsCache,
        guideRowsCache,
    },
});

export const setGridGuideOfferPreviewDataAction = (
    offerPreview: object,
    offerId: string,
): AnyAction => ({
    type: SET_GRID_GUIDE_OFFER_PREVIEW_DATA,
    payload: {
        offerPreview,
        offerId,
    },
});

export const guideSetIsCaretVisibleAction = (isCaretVisible: boolean): AnyAction => ({
    type: GUIDE_SET_IS_CARET_VISIBLE,
    payload: {
        isCaretVisible,
    },
});

export const guideSetIsSpinnerActiveAction = (isSpinnerActive: boolean): AnyAction => ({
    type: GUIDE_SET_IS_SPINNER_ACTIVE,
    payload: {
        isSpinnerActive,
    },
});
